/**
 * Copyright © 2025 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { Card, Grid, Box, TextField, Typography } from "@mui/material";
import BasicSelect from "../select_field";
import styled from "@emotion/styled";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { renderTimeViewClock } from "@mui/x-date-pickers/timeViewRenderers";
import { fetchShifts, addShifts } from "../../api/reservationServices";
import {
  MenuItem,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
} from "@mui/material";

const FormSelect = styled((props) => <BasicSelect {...props} />)`
  border: 0;
  border-bottom: 2px solid #e5e5e5;
  background-color: white;
`;

const FieldLabel = styled((props) => <Typography {...props} />)`
  color: #7c7c7c;
  font-size: 14px;
`;
const statuses = [
  { name: "N/A", value: "" },
  { name: "Canceled", value: "Canceled" },
  { name: "Assumed Done", value: "Assumed Done" },
  { name: "Assumed Seated", value: "Assumed Seated" },
  { name: "Done", value: "Done" },
  { name: "No Show", value: "No Show" },
  { name: "Paid", value: "Paid" },
  { name: "Seated Disputed", value: "Seated Disputed" },
];

const days = [
  { name: "Monday", value: 1 },
  { name: "Tuesday", value: 2 },
  { name: "Wednesday", value: 3 },
  { name: "Thursday", value: 4 },
  { name: "Friday", value: 5 },
  { name: "Saturday", value: 6 },
  { name: "Sunday", value: 7 },
];

const weekDays = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" },
];

const Calculator = ({ calculatedValues, values, handleChangeField }) => {
  const [hours, setHours] = React.useState("00");
  const [minutes, setMinutes] = React.useState("00");
  const [shifts, setShifts] = React.useState([]);
  const [selectedOption, setSelectedOption] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [newOption, setNewOption] = React.useState("");
  const [shiftSelectOpen, setShiftSelectOpen] = React.useState(true);
  const [formData, setFormData] = React.useState({
    name: "",
    startTime: null,
    endTime: null,
    startDay: "",
    endDay: "",
  });

  const [formDataError, setFormDataError] = React.useState({
    name: null,
    startTime: null,
    endTime: null,
    startDay: null,
    endDay: null,
  });

  const [formattedShifts, setFormattedShifts] = React.useState([]);

  React.useEffect(() => {
    const formattedShifts = shifts.map((option, index) => ({
      value: option.id,
      name: (
        <MenuItem
          sx={{
            width: "14rem",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "flex-start",
          }}
          key={index}
        >
          <div
            style={{
              display: shiftSelectOpen ? "none" : "flex",
              color: "black",
              marginLeft: "-20px",
            }}
          >
            {option?.name} {option?.start_time} - {option?.end_time}
          </div>
          <div
            style={{
              display: shiftSelectOpen ? "flex" : "none",
              color: "black",
              fontWeight: "600",
            }}
          >
            {option?.name}
          </div>
          {
            <div
              style={{
                display: shiftSelectOpen ? "flex" : "none",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <div style={{ textTransform: "capitalize" }}>
                {option?.start_day?.slice(0, 3)} -{" "}
                {option?.end_day?.slice(0, 3)}
              </div>
              <div style={{ marginLeft: "20%" }}>
                {option?.start_time} - {option?.end_time}
              </div>
            </div>
          }
        </MenuItem>
      ),
    }));
    formattedShifts.unshift({
      value: "",
      name: <MenuItem sx={{ fontWeight: "600" }}>All Shifts</MenuItem>,
    });
    formattedShifts.push({
      value: "",
      name: (
        <MenuItem sx={{ fontWeight: "600" }} onClick={handleOpen}>
          Add New Shift...
        </MenuItem>
      ),
    });
    setFormattedShifts(formattedShifts);
  }, [shifts, shiftSelectOpen]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleError = (field, value) => {
    setFormDataError((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const requiredFields = [
      "name",
      "startTime",
      "endTime",
      "startDay",
      "endDay",
    ];
    const errors = {};
    requiredFields.forEach((field) => {
      if (!formData[field]) {
        errors[field] = "This field is required";
        handleError(field, true);
      }
    });
    if (Object.keys(errors).length) {
      return;
    }
    try {
      const response = addShifts({
        name: formData.name,
        start_time: formData.startTime,
        end_time: formData.endTime,
        start_day: formData.startDay,
        end_day: formData.endDay,
      });
      console.log("Form submitted successfully:", response);
      handleClose();
      shiftList();
    } catch (error) {
      console.error("Error submitting form:", error);
    }
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setNewOption("All shifts");
  };

  const handleAddOption = () => {
    if (newOption.trim()) {
      setShifts([...shifts, newOption]);
      setNewOption("");
      setOpen(false);
    }
  };

  const updateShift = (shift) => {
    //   setShiftSelectOpen(false)
    setSelectedOption(shift);
    handleChangeField(shift, "shift_id");
  };

  const generateOptions = (end) => {
    const options = [];
    for (let i = 0; i <= end; i++) {
      const value = i < 10 ? `0${i}` : `${i}`;
      options.push({ value, name: value });
    }
    return options;
  };
  React.useEffect(
    () => {
      const total_seconds = hours * 3600 + minutes * 60;
      handleChangeField(total_seconds, "cancellationTime");
    },
    [hours, minutes]
  );

  const shiftList = () => {
    fetchShifts().then((res) => {
      setShifts(res);
    });
  };

  React.useEffect(
    () => {
      shiftList();
    },
    []
  );

  const changeHoursAndMinutes = (value, field) => {
    if (field === "status" && value === "Canceled") {
      setHours("00");
      setMinutes(45);
    } else {
      setHours("00");
      setMinutes("00");
    }
    handleChangeField(value, field);
  };
  const onKeyDown = (e) => {
    e.preventDefault();
  };

  const onSelectClose = () => {
    setShiftSelectOpen(false);
  };

  const onSelectOpen = () => {
    setShiftSelectOpen(true);
  };
  return (
    <Card
      variant="outlined"
      sx={{
        marginBottom: "20px",
        transition: "all .3s ease-in-out",
        borderRadius: "18px",
        border: "2px solid #DCDCDC",
      }}
    >
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle>Add New Shift</DialogTitle>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <form style={{ paddingTop: "20px" }} onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Name"
                    variant="outlined"
                    fullWidth
                    value={formData.name}
                    onChange={(e) => handleChange("name", e.target.value)}
                    error={!!formDataError?.name}
                    helperText={formDataError?.name ? "Name is required" : ""}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TimePicker
                    label="Start Time"
                    value={formData.startTime}
                    sx={{ width: "100%" }}
                    onChange={(newValue) => handleChange("startTime", newValue)}
                    viewRenderers={{
                      hours: renderTimeViewClock,
                      minutes: renderTimeViewClock,
                      seconds: renderTimeViewClock,
                    }}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                    error={!!formDataError?.startTime}
                    helperText={
                      formDataError?.startTime ? "Start time is required" : ""
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TimePicker
                    label="End Time"
                    value={formData.endTime}
                    sx={{ width: "100%" }}
                    viewRenderers={{
                      hours: renderTimeViewClock,
                      minutes: renderTimeViewClock,
                      seconds: renderTimeViewClock,
                    }}
                    onChange={(newValue) => handleChange("endTime", newValue)}
                    renderInput={(params) => (
                      <TextField {...params} fullWidth />
                    )}
                    error={!!formDataError?.endTime}
                    helperText={
                      formDataError?.endTime ? "End time is required" : ""
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    select
                    label="Start Day"
                    value={formData.startDay}
                    onChange={(e) => handleChange("startDay", e.target.value)}
                    fullWidth
                    variant="outlined"
                    error={!!formDataError?.startDay}
                    helperText={
                      formDataError?.startDay ? "Start day is required" : ""
                    }
                  >
                    {weekDays.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    select
                    label="End Day"
                    value={formData.endDay}
                    onChange={(e) => handleChange("endDay", e.target.value)}
                    fullWidth
                    variant="outlined"
                    error={!!formDataError?.endDay}
                    helperText={
                      formDataError?.endDay ? "End date is required" : ""
                    }
                  >
                    {weekDays.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="dark"
                    fullWidth
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>
            </form>
          </LocalizationProvider>
        </DialogContent>
      </Dialog>

      <Grid container>
        <Grid item xs={9}>
          <Box
            sx={{
              marginX: "20px",
              marginY: "30px",
              borderRight: "1px solid #DCDCDC",
              height: "80%",
            }}
          >
            <Grid container spacing={"20px"}>
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-around",
                  gap: "20px",
                  marginRight: "20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-betwen",
                    alignItems: "center",
                    gap: "30px",
                    width: "100%",
                  }}
                >
                  <FieldLabel sx={{ width: "38%" }}>Status: </FieldLabel>
                  <FormSelect
                    items={statuses}
                    value={values?.status}
                    handleSelect={(e) =>
                      changeHoursAndMinutes(e.target.value, "status")
                    }
                    label={"Status"}
                    required
                    variant="outlined"
                    borderRadius={"5px"}
                    width={"57%"}
                    minWidth={"52%"}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-betwen",
                    alignItems: "center",
                    gap: "30px",
                    width: "100%",
                  }}
                >
                  <FieldLabel sx={{ width: "38%" }}>
                    Days of the week:{" "}
                  </FieldLabel>
                  <FormSelect
                    items={days}
                    value={values?.days}
                    handleSelect={(e) =>
                      handleChangeField(e.target.value, "days")
                    }
                    label={"Days"}
                    required
                    multiple
                    variant="outlined"
                    borderRadius={"5px"}
                    width={"53%"}
                    minWidth={"52%"}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-betwen",
                    alignItems: "center",
                    gap: "30px",
                    width: "100%",
                  }}
                >
                  <FieldLabel sx={{ width: "35%" }}>
                    Minimum guests per table:{" "}
                  </FieldLabel>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      width: "57%",
                    }}
                  >
                    <TextField
                      placeholder={""}
                      required
                      value={values?.minGuestsPerTable}
                      onChange={(e) =>
                        handleChangeField(e.target.value, "minGuestsPerTable")
                      }
                      variant="outlined"
                      InputProps={{
                        style: {
                          borderRadius: "5px",
                          height: "40px",
                        },
                      }}
                    />
                  </div>
                </div>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-around",
                  gap: "20px",
                  marginRight: "20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-betwen",
                    alignItems: "center",
                    gap: "30px",
                    width: "100%",
                  }}
                >
                  <FieldLabel sx={{ fontWeight: 600 }}>
                    {"Visit Date: "}
                  </FieldLabel>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-betwen",
                    alignItems: "center",
                    gap: "30px",
                    width: "100%",
                  }}
                >
                  <FieldLabel sx={{ fontWeight: 600 }}>
                    {"Visit Time: "}
                  </FieldLabel>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-betwen",
                    alignItems: "center",
                    gap: "30px",
                    width: "100%",
                  }}
                >
                  <FieldLabel sx={{ width: "35%" }}>
                    {"Same Day Cancelation Time Limit before: "}
                  </FieldLabel>
                  <div
                    style={{
                      width: "50%",
                      display: "flex",
                      justifyContent: "space-between",
                      flexDirection: "row",
                    }}
                  >
                    <FormSelect
                      items={generateOptions(24)}
                      value={hours}
                      handleSelect={(e) => setHours(e.target.value)}
                      label={"Hrs"}
                      required
                      variant="standard"
                      minWidth={"35%"}
                      marginRight={"10px"}
                    />
                    <FormSelect
                      items={generateOptions(60)}
                      value={minutes}
                      handleSelect={(e) => setMinutes(e.target.value)}
                      label={"Min"}
                      required
                      variant="standard"
                      minWidth={"35%"}
                      marginRight={"10px"}
                    />
                  </div>
                </div>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-around",
                  gap: "20px",
                  marginRight: "20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-betwen",
                    alignItems: "center",
                    gap: "30px",
                    width: "100%",
                  }}
                >
                  <FieldLabel sx={{ width: "38%" }}>
                    {"From (Date): "}
                  </FieldLabel>
                  <div style={{ width: "53%" }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={values?.vistFromDate}
                        closeOnSelect={true}
                        slotProps={{
                          textField: {
                            inputProps: {
                              style: {
                                borderRadius: "5px",
                                height: "10px",
                              },
                            },
                          },
                        }}
                        renderInput={(params) => (
                          <TextField onKeyDown={onKeyDown} {...params} />
                        )}
                        onChange={(newVal) =>
                          handleChangeField(newVal, "vistFromDate")
                        }
                      />
                    </LocalizationProvider>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-betwen",
                    alignItems: "center",
                    gap: "30px",
                    width: "100%",
                  }}
                >
                  <FieldLabel sx={{ width: "38%" }}>
                    {"From (Time): "}
                  </FieldLabel>
                  <div style={{ width: "53%" }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <TimePicker
                        value={values?.vistFromTime}
                        viewRenderers={{
                          hours: renderTimeViewClock,
                          minutes: renderTimeViewClock,
                          seconds: renderTimeViewClock,
                        }}
                        slotProps={{
                          textField: {
                            inputProps: {
                              style: {
                                borderRadius: "5px",
                                height: "10px",
                              },
                            },
                          },
                        }}
                        onChange={(newVal) =>
                          handleChangeField(newVal, "vistFromTime")
                        }
                      />
                    </LocalizationProvider>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-betwen",
                    alignItems: "center",
                    gap: "30px",
                    width: "100%",
                  }}
                >
                  <FieldLabel sx={{ width: "35%" }}>
                    {"Resale PPA: "}
                  </FieldLabel>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      width: "57%",
                    }}
                  >
                    <TextField
                      placeholder={"$"}
                      required
                      value={values?.resalePpa}
                      onChange={(e) =>
                        handleChangeField(e.target.value, "resalePpa")
                      }
                      variant="outlined"
                      InputProps={{
                        style: {
                          borderRadius: "5px",
                          height: "40px",
                        },
                      }}
                    />
                  </div>
                </div>
              </Grid>
              <Grid
                item
                xs={12}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-betwen",
                  gap: "20px",
                  marginRight: "20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-betwen",
                    alignItems: "center",
                    gap: "30px",
                    width: "100%",
                  }}
                >
                  <FieldLabel sx={{ width: "38%" }}>{"To (Date): "}</FieldLabel>
                  <div style={{ width: "53%" }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={values?.vistToDate}
                        closeOnSelect={true}
                        slotProps={{
                          textField: {
                            inputProps: {
                              style: {
                                borderRadius: "5px",
                                height: "10px",
                              },
                            },
                          },
                        }}
                        renderInput={(params) => (
                          <TextField onKeyDown={onKeyDown} {...params} />
                        )}
                        onChange={(newVal) =>
                          handleChangeField(newVal, "vistToDate")
                        }
                      />
                    </LocalizationProvider>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-betwen",
                    alignItems: "center",
                    gap: "30px",
                    width: "100%",
                  }}
                >
                  <FieldLabel sx={{ width: "38%" }}>{"To (Time): "}</FieldLabel>
                  <div style={{ width: "53%" }}>
                    <LocalizationProvider
                      sx={{ width: "50%" }}
                      dateAdapter={AdapterDayjs}
                    >
                      <TimePicker
                        value={values?.vistToTime}
                        viewRenderers={{
                          hours: renderTimeViewClock,
                          minutes: renderTimeViewClock,
                          seconds: renderTimeViewClock,
                        }}
                        slotProps={{
                          textField: {
                            inputProps: {
                              style: {
                                borderRadius: "5px",
                                height: "10px",
                                // padding: '5px',
                              },
                            },
                          },
                        }}
                        onChange={(newVal) =>
                          handleChangeField(newVal, "vistToTime")
                        }
                      />
                    </LocalizationProvider>
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-betwen",
                    alignItems: "center",
                    gap: "30px",
                    width: "100%",
                  }}
                >
                  <FieldLabel sx={{ width: "35%" }}>{"PPA: "}</FieldLabel>
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      width: "57%",
                    }}
                  >
                    <TextField
                      placeholder={"$"}
                      required
                      value={values?.ppa}
                      onChange={(e) => handleChangeField(e.target.value, "ppa")}
                      variant="outlined"
                      InputProps={{
                        style: {
                          borderRadius: "5px",
                          height: "40px",
                        },
                      }}
                    />
                  </div>
                </div>
              </Grid>
              <Grid
                item
                xs={4}
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-around",
                  gap: "20px",
                  marginRight: "20px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-betwen",
                    alignItems: "center",
                    gap: "30px",
                    width: "100%",
                  }}
                >
                  <FieldLabel sx={{ width: "38%" }}>Shift: </FieldLabel>
                  <FormSelect
                    items={formattedShifts}
                    onSelectClose={onSelectClose}
                    onSelectOpen={onSelectOpen}
                    handleSelect={(e) => updateShift(e.target.value)}
                    label={"Shifts"}
                    required
                    variant="outlined"
                    borderRadius={"5px"}
                    handleClick={() => setShiftSelectOpen(true)}
                    paddingTop={shiftSelectOpen ? "25px" : ""}
                    overflow={shiftSelectOpen ? "hidden" : ""}
                    width={"57%"}
                    minWidth={"52%"}
                  />
                </div>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        <Grid
          item
          xs={3}
          sx={{
            marginY: "30px",
            height: "80%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Card
            variant="outlined"
            sx={{
              marginRight: "30px",
              borderRadius: "8px",
              border: "1px solid #DCDCDC",
              padding: "20px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "row", width: "100%" }}>
              <FieldLabel sx={{ width: "75%" }}>
                {"Total reservations: "}
              </FieldLabel>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "25%",
                }}
              >
                <FieldLabel
                  sx={{ fontWeight: 600, color: "#1976d2 !important" }}
                >{`${calculatedValues?.total_reservation}`}</FieldLabel>
              </div>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row", width: "100%" }}>
              <FieldLabel sx={{ width: "75%" }}>{"Total guests: "}</FieldLabel>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "25%",
                }}
              >
                <FieldLabel
                  sx={{ fontWeight: 600, color: "#1976d2 !important" }}
                >{`${calculatedValues?.total_guests}`}</FieldLabel>
              </div>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row", width: "100%" }}>
              <FieldLabel sx={{ width: "75%" }}>
                {"Total value of canceled reservations: "}
              </FieldLabel>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "25%",
                }}
              >
                <FieldLabel
                  sx={{ fontWeight: 600, color: "red !important" }}
                >{`$ ${calculatedValues?.total_value}`}</FieldLabel>
              </div>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row", width: "100%" }}>
              <FieldLabel sx={{ marginTop: "20px", width: "75%" }}>
                {"Total resale value: "}
              </FieldLabel>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "25%",
                }}
              >
                <FieldLabel
                  sx={{
                    marginTop: "20px",
                    fontWeight: 600,
                    color: "black !important",
                  }}
                >{`$ ${calculatedValues?.resale_value}`}</FieldLabel>
              </div>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "row", width: "100%" }}>
              <FieldLabel
                sx={{ marginTop: "50px", fontWeight: 600, width: "75%" }}
              >
                {"Increased sales to restaurant: "}
              </FieldLabel>
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  width: "25%",
                }}
              >
                <FieldLabel
                  sx={{
                    marginTop: "50px",
                    fontWeight: 600,
                    color: "#2e7d32 !important",
                  }}
                >{`$ ${calculatedValues?.increase_sale}`}</FieldLabel>
              </div>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Card>
  );
};

export default Calculator;
